import { useEffect, useState } from 'react';
import { useCart } from '@v2/shared/lib/cart';
import { ExpandedProduct, OfferCodeSku } from '@v2/shared/lib/types';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { wafFetch } from '@v2/shared/lib/wafFetch';

type Options = {
  product: ExpandedProduct;
  offerCode: string | null;
  offerCodeSkus: OfferCodeSku[];
};

type Offer = {
  /** As on Props: the code `offerCodeSkus` came from, or null when none applied. */
  offerCode: string | null;
  offerCodeSkus: OfferCodeSku[];
  /** A sticky code is known and its prices are on their way. */
  isActivating: boolean;
};

/**
 * The offer the page is priced under: the server's, or — when the server had none — the
 * code the visitor's session holds for this product, once the cart has said what it is.
 * What headless_jshop's useProductView did.
 *
 * That second one is how a Google automated discount reaches this page. The ad's url
 * carries a signed `pv2` token, and it is the cart request that turns it into a code and
 * keeps it against the product — affiliateTracking.php, reading the page's url off the
 * Referer. The page request never looks at the token, so it renders the same for every
 * visitor and stays cacheable; the price moves here, after the cart arrives.
 *
 * A sticky code that prices nothing leaves the page as it was, for the reason the server
 * gives for its own: add-to-cart sends this code, and the cart should only be sent one
 * the page has shown the price of.
 */
export default function useStickyOffer({ product, offerCode, offerCodeSkus }: Options): Offer {
  const { cart } = useCart();
  const { buildUrl } = useUrlGenerator();
  const [fetched, setFetched] = useState<{ code: string; skus: OfferCodeSku[] } | null>(null);
  const stickyOfferCode = offerCode ? null : cart?.sticky_offer_codes?.[product.id] ?? null;

  // Every format the page can switch to, as the props builder asks for its own code.
  const slugs = Array.from(new Set([product.slug, ...(product.grouped_products ?? []).map(({ slug }) => slug)]));
  const query = slugs.map((slug) => `products[]=${encodeURIComponent(slug)}`).join('&');
  const url = stickyOfferCode
    ? buildUrl(`/services_api/storefront/v1/offer_codes/${encodeURIComponent(stickyOfferCode)}/skus.json?${query}`)
    : null;

  useEffect(() => {
    if (!url || !stickyOfferCode) {
      return;
    }

    let cancelled = false;

    wafFetch(url)
      .then((response) => (response.ok ? response.json() : { results: [] }))
      .catch(() => ({ results: [] }))
      .then((body: { results?: OfferCodeSku[] }) => {
        if (!cancelled) {
          setFetched({ code: stickyOfferCode, skus: body.results ?? [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!stickyOfferCode) {
    return { offerCode, offerCodeSkus, isActivating: false };
  }

  if (fetched?.code !== stickyOfferCode) {
    return { offerCode, offerCodeSkus, isActivating: true };
  }

  return fetched.skus.length
    ? { offerCode: stickyOfferCode, offerCodeSkus: fetched.skus, isActivating: false }
    : { offerCode, offerCodeSkus, isActivating: false };
}
